import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ImageGallery from '../components/UI/ImageGallery';
import Button from '../components/UI/Button';

const GalleryScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Organize images with categories for better UX
  const galleryImages = [
    // Exterior Views
    { src: '/assets/images/villa Baidane-01.jpg', alt: 'Villa exterior view', title: 'Villa Entrance', description: 'Welcome to Village Baidane', category: 'exterior' },
    { src: '/assets/images/villa Baidane-02.jpg', alt: 'Villa garden', title: 'Beautiful Gardens', description: 'Lush green spaces surrounding the villa', category: 'exterior' },
    { src: '/assets/images/villa Baidane-03.jpg', alt: 'Villa architecture', title: 'Moroccan Architecture', description: 'Traditional design meets modern comfort', category: 'exterior' },
    { src: '/assets/images/villa Baidane-30.jpg', alt: 'Villa landscape', title: 'Landscape View', description: 'Stunning views of the Atlas Mountains', category: 'exterior' },
    
    // Pool & Garden
    { src: '/assets/images/villa Baidane-15.jpg', alt: 'Swimming pool', title: 'Heated Pool', description: '15x5m heated swimming pool', category: 'pool' },
    { src: '/assets/images/villa Baidane-16.jpg', alt: 'Pool area', title: 'Pool Terrace', description: 'Relaxing poolside area with loungers', category: 'pool' },
    { src: '/assets/images/villa Baidane-17.jpg', alt: 'Garden view', title: 'Garden Paradise', description: 'Peaceful garden spaces for relaxation', category: 'pool' },
    { src: '/assets/images/villa Baidane-35.jpg', alt: 'Outdoor dining', title: 'Al Fresco Dining', description: 'Outdoor dining with mountain views', category: 'pool' },
    
    // Interior Spaces
    { src: '/assets/images/villa Baidane-18.jpg', alt: 'Living room', title: 'European Living Room', description: 'Spacious living area with fireplace', category: 'interior' },
    { src: '/assets/images/villa Baidane-19.jpg', alt: 'Moroccan salon', title: 'Traditional Salon', description: 'Authentic Moroccan sitting area', category: 'interior' },
    { src: '/assets/images/villa Baidane-20.jpg', alt: 'Bedroom', title: 'Master Bedroom', description: 'Comfortable bedroom with en-suite', category: 'interior' },
    { src: '/assets/images/villa Baidane-21.jpg', alt: 'Kitchen', title: 'Fully Equipped Kitchen', description: 'Modern kitchen with all amenities', category: 'interior' },
    { src: '/assets/images/villa Baidane-22.jpg', alt: 'Bathroom', title: 'Luxury Bathroom', description: 'Spacious bathroom with bathtub', category: 'interior' },
    
    // Amenities & Activities
    { src: '/assets/images/villa Baidane-38.jpg', alt: 'Dining setup', title: 'Dining Experience', description: 'Traditional Moroccan dining', category: 'amenities' },
    { src: '/assets/images/villa Baidane-40.jpg', alt: 'Yoga space', title: 'Yoga & Wellness', description: 'Peaceful spaces for yoga and meditation', category: 'amenities' },
    { src: '/assets/images/villa Baidane-42.jpg', alt: 'Desert view', title: 'Desert Adventures', description: 'Nearby Agafay Desert experiences', category: 'amenities' },
    
    // Additional Villa Views
    { src: '/assets/images/villa-nadia/villa-nadia-00001.webp', alt: 'Villa Nadia', title: 'Villa Nadia', description: 'European-style villa with 4 bedrooms and heated pool', category: 'exterior' },
    { src: '/assets/images/villa-nadia/villa-nadia-00008.webp', alt: 'Villa Nadia pool', title: 'Villa Nadia Pool', description: 'Heated swimming pool and garden terrace', category: 'pool' },
    { src: '/assets/images/villa-nadia/villa-nadia-00012.webp', alt: 'Villa Nadia interior', title: 'Villa Nadia Interior', description: 'Spacious European-style living spaces', category: 'interior' },
    { src: '/assets/images/villa Baidane-33.jpg', alt: 'Property view', title: 'Property Overview', description: '12 hectares of beautiful landscape', category: 'exterior' },
    { src: '/assets/images/villa Baidane-36.jpg', alt: 'Sunset view', title: 'Magical Sunsets', description: 'Breathtaking sunset views', category: 'exterior' },
  ];

  return (
    <div className="min-h-screen ">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat mix-blend-overlay"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-01.jpg')`
          }}
        ></div>
        
        {/* Floating Elements */}
        <div className="absolute top-16 left-8 w-16 h-16 bg-white/10 rounded-full animate-float"></div>
        <div className="absolute bottom-20 right-12 w-12 h-12 bg-accent/30 rounded-full animate-float" style={{animationDelay: '1s'}}></div>

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-sofia font-bold mb-6 animate-fade-in-up">
            {t('gallery.title')}
          </h1>
          <p className="text-xl md:text-2xl font-sofia font-light animate-fade-in-up opacity-90" style={{animationDelay: '0.2s'}}>
            {t('gallery.subtitle')}
          </p>
        </div>
      </section>

      {/* Gallery Stats */}
      <section className="py-16" style={{ background: 'url("/assets/icons/top-img-bg.png") ' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="animate-fade-in-up">
              <div className="text-4xl font-sofia font-bold text-[#81736d] mb-2">450m²</div>
              <div className="font-sofia text-black">{t('gallery.stats.livingSpace')}</div>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
              <div className="text-4xl font-sofia font-bold text-[#81736d] mb-2">12</div>
              <div className="font-sofia text-black">{t('gallery.stats.hectares')}</div>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              <div className="text-4xl font-sofia font-bold text-[#81736d] mb-2">2</div>
              <div className="font-sofia text-black">{t('gallery.stats.luxuryVillas')}</div>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: '0.3s'}}>
              <div className="text-4xl font-sofia font-bold text-[#81736d] mb-2">20km</div>
              <div className="font-sofia text-black">{t('gallery.stats.fromMarrakech')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Gallery */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl font-sofia font-bold text-black mb-6">
              {t('gallery.exploreSpaces.title')}
            </h2>
            <p className="text-lg font-sofia text-text-secondary max-w-3xl mx-auto">
              {t('gallery.exploreSpaces.description')}
            </p>
          </div>

          <div className="animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            <ImageGallery images={galleryImages} columns={3} />
          </div>
        </div>
      </section>

     

      {/* Instagram Feed Teaser */}

      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-05.jpg')`,
          }}
        ></div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50 "></div>
        {/* <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-accent/90"></div> */}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-sofia font-bold mb-6">
            {t('gallery.instagram.title')}
          </h2>
          <p className="text-xl font-sofia font-light mb-8 max-w-3xl mx-auto">
            {t('gallery.instagram.description')}
          </p>

        

          <div className="space-x-4">
            <Button 
              variant="outline" 
              className="hover:scale-105 transform transition-all duration-300 text-white border-white"
              onClick={() => window.open('https://instagram.com/villagebaidane', '_blank')}
            >
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  className="h-5 w-5 mx-1"
                >
                  <path d="M7.75 2A5.75 5.75 0 0 0 2 7.75v8.5A5.75 5.75 0 0 0 7.75 22h8.5A5.75 5.75 0 0 0 22 16.25v-8.5A5.75 5.75 0 0 0 16.25 2h-8.5Zm0 1.5h8.5A4.25 4.25 0 0 1 20.5 7.75v8.5a4.25 4.25 0 0 1-4.25 4.25h-8.5a4.25 4.25 0 0 1-4.25-4.25v-8.5A4.25 4.25 0 0 1 7.75 3.5Zm8.75 2a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5ZM12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm0 1.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7Z" />
                </svg>
              {t('gallery.instagram.followUs')}
            </Button>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default GalleryScreen;
